import React, { useState } from 'react';
import { 
  Download, 
  Play, 
  Pause, 
  FileText, 
  Image, 
  Video, 
  Music, 
  User,
  Check,
  CheckCheck,
  Clock,
  RotateCcw
} from 'lucide-react';
import { 
  isImageFile, 
  isVideoFile, 
  isAudioFile, 
  isDocumentFile,
  formatFileSize,
  getFileExtension
} from '../utils/fileUtils';

const MessageBubble = ({ 
  message, 
  isOwnMessage, 
  onMediaClick, 
  onDownload 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedControls, setShowSpeedControls] = useState(false);
  
  const audioRef = React.useRef(null);

  // Load audio duration when component mounts or message changes
  React.useEffect(() => {
    if (audioRef.current && message.fileUrl) {
      const audio = audioRef.current;
      
      const loadDuration = () => {
        if (audio.duration && !isNaN(audio.duration) && audio.duration !== Infinity) {
          console.log('🎵 Duration loaded:', audio.duration);
          setDuration(audio.duration);
        }
      };

      // Try to get duration immediately if already loaded
      if (audio.readyState >= 1) {
        loadDuration();
      } else {
        // Wait for metadata to load
        audio.addEventListener('loadedmetadata', loadDuration);
        audio.addEventListener('canplay', loadDuration);
        
        return () => {
          audio.removeEventListener('loadedmetadata', loadDuration);
          audio.removeEventListener('canplay', loadDuration);
        };
      }
    }
  }, [message.fileUrl]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    try {
      let date;
      
      // Handle Firebase Timestamp objects
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        // Handle Firebase Timestamp with toDate method
        date = timestamp.toDate();
      } else {
        // Handle regular Date objects or ISO strings
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) return 'Just now';
      
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting timestamp:', error, timestamp);
      return 'Just now';
    }
  };

  const getMessageStatus = () => {
    if (message.status === 'sent') return <Clock className="w-3 h-3" />;
    if (message.status === 'delivered') return <Check className="w-3 h-3" />;
    if (message.status === 'read') return <CheckCheck className="w-3 h-3 text-blue-500" />;
    return <Clock className="w-3 h-3" />;
  };

  const getFileIcon = (fileName) => {
    if (isImageFile(fileName)) {
      return <Image className="w-4 h-4" />;
    }
    if (isVideoFile(fileName)) {
      return <Video className="w-4 h-4" />;
    }
    if (isAudioFile(fileName)) {
      return <Music className="w-4 h-4" />;
    }
    return <FileText className="w-4 h-4" />;
  };

  const handleAudioPlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.playbackRate = playbackSpeed;
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;
      console.log('🎵 Audio loaded - Duration:', audioDuration);
      if (audioDuration && !isNaN(audioDuration) && audioDuration !== Infinity) {
        setDuration(audioDuration);
      } else {
        // Try to get duration from message metadata if available
        if (message.duration) {
          setDuration(message.duration);
        } else {
          setDuration(0);
        }
      }
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const formatAudioTime = (time) => {
    if (!time || isNaN(time) || time === Infinity) {
      return '0:00';
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const renderMessageContent = () => {
    // Debug logging to understand message structure
    console.log('🔍 MessageBubble - Message structure:', {
      type: message.type,
      fileType: message.fileType,
      content: message.content,
      text: message.text,
      fileUrl: message.fileUrl,
      isImage: isImageFile(message.fileType || message.content || message.text),
      isVideo: isVideoFile(message.fileType || message.content || message.text),
      isAudio: isAudioFile(message.fileType || message.content || message.text),
      fullMessage: message
    });

    // Text message - only if it's not a file/image
    if ((message.type === 'text' || message.content) && 
        !message.fileUrl && 
        !isImageFile(message.content || message.text) && 
        !isVideoFile(message.content || message.text) && 
        !isAudioFile(message.content || message.text)) {
      return (
        <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
          {message.content || message.text}
        </p>
      );
    }

    // Image message - Check multiple conditions
    const isImageMessage = 
      message.type === 'image' || 
      (message.fileUrl && isImageFile(message.fileType || message.content || message.text)) ||
      (message.type === 'file' && message.fileType?.startsWith('image/')) ||
      (message.type === 'file' && isImageFile(message.content || message.text)) ||
      (message.fileUrl && message.fileType?.startsWith('image/')) ||
      // Additional checks for common image extensions
      (message.content && isImageFile(message.content)) ||
      (message.text && isImageFile(message.text)) ||
      // Check if filename contains image extensions
      (message.content && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(message.content)) ||
      (message.text && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(message.text)) ||
      // Check if the message content looks like an image filename
      (message.content && typeof message.content === 'string' && message.content.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i)) ||
      (message.text && typeof message.text === 'string' && message.text.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i));

    if (isImageMessage) {
      // If we don't have a fileUrl, show as file attachment
      if (!message.fileUrl) {
        console.log('🔍 Image message without fileUrl:', message);
        
        // Try to construct a possible file URL based on the filename
        const filename = message.content || message.text;
        const possibleFileUrl = `https://firebasestorage.googleapis.com/v0/b/nyaysahayak-d6c6e.appspot.com/o/chats%2F${message.chatId || 'unknown'}%2Ffiles%2F${filename}?alt=media`;
        
        return (
          <div className="space-y-2">
            <div 
              className="flex items-center space-x-3 p-3 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={() => onMediaClick({
                url: possibleFileUrl,
                name: message.content || message.text || 'Image',
                type: message.fileType || 'image/jpeg',
                size: message.fileSize
              })}
            >
              <Image className="w-4 h-4" />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
                  {message.content || message.text || 'Image'}
                </p>
                <p className="text-xs text-gray-500">Image file (no URL)</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(possibleFileUrl, message.content || message.text);
                }}
                className="p-1 hover:bg-gray-300 rounded transition-colors"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      }

      // If we have fileUrl, show as image
      return (
        <div className="space-y-2">
          <img
            src={message.fileUrl}
            alt="attachment"
            className="max-w-[200px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => onMediaClick({
              url: message.fileUrl,
              name: message.content || message.text || 'Image',
              type: message.fileType || 'image/jpeg',
              size: message.fileSize
            })}
          />
          {message.content && (
            <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
              {message.content}
            </p>
          )}
        </div>
      );
    }

    // Video message - Check multiple conditions
    const isVideoMessage = 
      message.type === 'video' || 
      (message.fileUrl && isVideoFile(message.fileType || message.content || message.text)) ||
      (message.type === 'file' && message.fileType?.startsWith('video/')) ||
      (message.type === 'file' && isVideoFile(message.content || message.text)) ||
      (message.fileUrl && message.fileType?.startsWith('video/')) ||
      // Additional checks for common video extensions
      (message.content && isVideoFile(message.content)) ||
      (message.text && isVideoFile(message.text)) ||
      // Check if filename contains video extensions
      (message.content && /\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i.test(message.content)) ||
      (message.text && /\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i.test(message.text));

    if (isVideoMessage) {
      // If we don't have a fileUrl, show as file attachment
      if (!message.fileUrl) {
        return (
          <div className="space-y-2">
            <div 
              className="flex items-center space-x-3 p-3 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={() => onMediaClick({
                url: message.fileUrl,
                name: message.content || message.text || 'Video',
                type: message.fileType || 'video/mp4',
                size: message.fileSize
              })}
            >
              <Video className="w-4 h-4" />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
                  {message.content || message.text || 'Video'}
                </p>
                <p className="text-xs text-gray-500">Video file</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(message.fileUrl, message.content || message.text);
                }}
                className="p-1 hover:bg-gray-300 rounded transition-colors"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      }

      // If we have fileUrl, show as video
      return (
        <div className="space-y-2">
          <div className="relative">
            <video
              src={message.fileUrl}
              className="max-w-[200px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => onMediaClick({
                url: message.fileUrl,
                name: message.content || message.text || 'Video',
                type: message.fileType || 'video/mp4',
                size: message.fileSize
              })}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black bg-opacity-50 rounded-full p-2">
                <Play className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          {message.content && (
            <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
              {message.content}
            </p>
          )}
        </div>
      );
    }

    // Audio/Voice message - WhatsApp Style
    if (message.type === 'voice' || message.type === 'audio' || (message.fileUrl && (isAudioFile(message.fileType || message.content || message.text) || message.fileType?.startsWith('audio/') || (message.type === 'file' && message.fileType?.startsWith('audio/'))))) {
      return (
        <div className="space-y-2">
          <div className="flex items-center space-x-3 min-w-[200px] max-w-[280px]">
            {/* Profile Icon with Microphone */}
            <div className="relative">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isOwnMessage ? 'bg-purple-600' : 'bg-gray-400'
              }`}>
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Play Button */}
            <button
              onClick={handleAudioPlay}
              className={`p-2 rounded-full transition-colors ${
                isOwnMessage 
                  ? 'bg-green-500 hover:bg-green-600 text-white' 
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>

            {/* Waveform/Progress Bar */}
            <div className="flex-1">
              <div className="flex items-end space-x-0.5 h-8">
                {/* Generate waveform bars based on duration */}
                {Array.from({ length: 20 }, (_, i) => {
                  const barHeight = isPlaying && currentTime > 0 
                    ? Math.min(1, (currentTime / duration) * 20 > i ? 0.8 : 0.3)
                    : 0.3 + Math.sin(i * 0.3) * 0.4;
                  
                  return (
                    <div
                      key={i}
                      className={`rounded-sm transition-all duration-200 ${
                        isOwnMessage ? 'bg-green-400' : 'bg-purple-400'
                      }`}
                      style={{
                        width: '2px',
                        height: `${barHeight * 100}%`,
                        opacity: isPlaying && currentTime > 0 && (currentTime / duration) * 20 > i ? 1 : 0.6
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Duration */}
            <div className={`text-sm font-medium ${
              isOwnMessage ? 'text-white' : 'text-gray-900'
            }`}>
              {duration > 0 ? formatAudioTime(duration) : '0:00'}
            </div>

            {/* Speed Control */}
            <button
              onClick={() => setShowSpeedControls(!showSpeedControls)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                isOwnMessage 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {playbackSpeed}x
            </button>
          </div>

          {/* Speed Controls Dropdown */}
          {showSpeedControls && (
            <div className="mt-2 flex items-center justify-center space-x-1">
              {[0.5, 1, 1.5, 2].map((speed) => (
                <button
                  key={speed}
                  onClick={() => handleSpeedChange(speed)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    playbackSpeed === speed
                      ? (isOwnMessage ? 'bg-green-600 text-white' : 'bg-purple-600 text-white')
                      : (isOwnMessage ? 'bg-green-200 text-green-800' : 'bg-purple-200 text-purple-800')
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          )}

          {/* Hidden Audio Element */}
          <audio
            ref={audioRef}
            src={message.fileUrl || message.audioUrl}
            onTimeUpdate={handleAudioTimeUpdate}
            onLoadedMetadata={handleAudioLoadedMetadata}
            onEnded={handleAudioEnded}
            className="hidden"
          />
        </div>
      );
    }

    // Document/File message
    if (message.type === 'document' || message.type === 'file' || (message.fileUrl && isDocumentFile(message.fileType || message.content || message.text))) {
      return (
        <div className="space-y-2">
          <div 
            className="flex items-center space-x-3 p-3 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
            onClick={() => onMediaClick({
              url: message.fileUrl,
              name: message.content || message.text || 'Document',
              type: message.fileType || 'application/octet-stream',
              size: message.fileSize
            })}
          >
            {getFileIcon(message.content || message.text)}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
                {message.content || message.text || 'Document'}
              </p>
              {message.fileSize && (
                <p className="text-xs text-gray-500">
                  {message.fileSize}
                </p>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownload(message.fileUrl, message.content || message.text);
              }}
              className="p-1 hover:bg-gray-300 rounded transition-colors"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    // System message
    if (message.type === 'system') {
      return (
        <div className="text-center">
          <p className="text-xs text-gray-500 bg-gray-100 rounded-full px-3 py-1 inline-block">
            {message.content}
          </p>
        </div>
      );
    }

    // Default text fallback
    return (
      <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
        {message.content || message.text || 'Message'}
      </p>
    );
  };

  // System message styling
  if (message.type === 'system') {
    return (
      <div className="flex justify-center my-2">
        {renderMessageContent()}
      </div>
    );
  }

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex items-end space-x-2 max-w-xs lg:max-w-md ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}>
        {/* Avatar */}
        {!isOwnMessage && (
          <div className="flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
              <User className="w-4 h-4 text-gray-600" />
            </div>
          </div>
        )}
        
        {/* Message Bubble */}
        <div
          className={`px-4 py-2 rounded-lg ${
            isOwnMessage
              ? 'bg-purple-600 text-white'
              : 'bg-white text-gray-900 border border-gray-200'
          }`}
        >
          {renderMessageContent()}
          
          {/* Timestamp and Status */}
          <div className={`flex items-center justify-between mt-1 space-x-2 ${
            isOwnMessage ? 'text-purple-200' : 'text-gray-500'
          }`}>
            <span className="text-xs">
              {formatTime(message.createdAt || message.timestamp)}
            </span>
            {isOwnMessage && (
              <div className="flex items-center">
                {getMessageStatus()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
